//@ts-nocheck
import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import formElementMixin from "@banno/jha-wc/src/js/mixins/form-element-mixin";
import { WorkflowTemplateGeneralViewMode } from "../../types/WorkflowTemplates/WorkflowTemplateTypeIndex";
import { getRequest } from "../../utils/jha-fetch";
import { Apis } from "../../utils/api-constants";
import { stringComparison } from "../../utils/app-utils";
import "./WorkflowTemplateStepsView";
import { WorkflowTemplateVariable } from "../../types/WorkflowTemplates/WorkflowTemplateVariable";
import { fileLocationAuthTypeForApiStep } from "../../utils/workflow-file-location-secret";
import {
    flattenFileInfoFromStep,
    nestFileInfoIntoStep,
    readStepFilePath,
    storageTypeUsesStepCredentials,
} from "../../utils/workflow-file-info-leg";
import { toPersistedTemplatePullSmbBucketName } from "../../utils/workflowTemplatePlaceholderSyntax";
import { mapLegacyLambdaFunctionName } from "../../utils/step-function-display";

/**
 * Workflow Template Steps Tab
 *
 * This tab manages workflow step definitions for templates.
 * Uses WorkflowTemplateStepsView with styling matching the Variables and Notifications grids.
 *
 * Features:
 * - Add/Edit/Delete steps using the standard step popup dialogs
 * - Behavior changes based on TransferType (Push vs Pull)
 * - Push: Shows "Delete files from Source Location" checkbox
 * - Pull: Different step handling
 *
 * Validation:
 * - Steps are optional - tab is valid even with no steps
 * - Tab tracks dirty state for unsaved changes
 */
@customElement("workflow-template-steps-tab")
export class WorkflowTemplateStepsTab extends formElementMixin(LitElement) {
    static get is() {
        return "workflow-template-steps-tab";
    }

    @property({ type: String }) mode: WorkflowTemplateGeneralViewMode = "ADD";
    @property({ type: String }) workflowTemplateId: string = "";
    @property({ type: Boolean }) isReadOnly: boolean = false;
    @property({ type: Object }) templateData: any = null; // Passed from parent - no need to fetch
    @property({ type: String }) transferType: string = "";

    // ============================================
    // TEMPLATE VARIABLE SUPPORT
    // These properties enable template-string-input components in the
    // AddWorkflowStepPopup dialog. Variables are passed from GeneralView
    // down through this tab to StepsView and finally to the popup.
    // ============================================

    /**
     * Template variables available for use in step fields.
     * Received from WorkflowTemplateGeneralView and passed to WorkflowTemplateStepsView.
     */
    @property({ type: Array }) variables: WorkflowTemplateVariable[] = [];

    /**
     * [ACME-1422] Friendly Business Unit display name passed down from
     * `WorkflowTemplateGeneralView` (Settings tab). Forwarded to
     * `WorkflowTemplateStepsView` so the SMB bucket preview in
     * `AddWorkflowStepPopup` matches the API-persisted `{env}-smb-{slug}` shape.
     */
    @property({ type: String }) buDisplayName: string = "";

    /**
     * Handle variable-added event from WorkflowTemplateStepsView.
     * When a user creates a new variable inline within the step popup,
     * this propagates the event up to WorkflowTemplateGeneralView
     * where it's added to the Settings tab's variable list.
     */
    onVariableAdded(e: CustomEvent) {
        e.stopPropagation();
        this.dispatchEvent(
            new CustomEvent("variable-added", {
                detail: e.detail,
                bubbles: true,
                composed: true,
            })
        );
    }

    // Steps data
    @state() workflowStepsData: any[] = [];
    @state() deleteFiles: boolean = true;

    // Form data to pass to WorkflowStepsView
    @state() formData: any = {
        workflowName: "",
        TransferType: "",
        BusinessUnitParticipant: "",
        sourceLocationPath: "",
        TriggerFilePattern: [],
        DataFilePattern: [],
        valid: false,
        isEdit: false,
        DeleteFiles: true,
    };

    // Loading state
    @state() apiLoading: boolean = false;

    // Dirty state
    @state() isDirty: boolean = false;

    // Mid-edit state: true when step popup is open
    @state() stepMidEdit: boolean = false;

    // Flag to prevent marking dirty during initial load
    private isInitializing: boolean = true;

    // Flag to prevent double-loading steps
    private stepsLoaded: boolean = false;

    // Original data for dirty detection
    private originalStepsData: any[] = [];

    static readonly styles = css`
    :host {
      display: block;
      padding: 20px;
    }

    h2 {
      margin-top: 0;
      margin-bottom: 20px;
      font-size: 24px;
      font-weight: 600;
    }

    @media (prefers-color-scheme: dark) {
      h2 {
        color: #a3d4ff;
      }
    }

    @media (prefers-color-scheme: light) {
      h2 {
        color: #06185f;
      }
    }

    .info-text {
      color: var(--jha-text-light);
      font-size: 14px;
      margin-bottom: 16px;
    }

    .no-transfer-type {
      padding: 32px;
      text-align: center;
      border: 1px solid;
      border-radius: 4px;
    }

    @media (prefers-color-scheme: light) {
      .no-transfer-type {
        color: #666666;
        border-color: #e0e0e0;
        background-color: #f5f5f5;
      }
    }

    @media (prefers-color-scheme: dark) {
      .no-transfer-type {
        color: #a0a0a0;
        border-color: #3a3a3a;
        background-color: #2a2a2a;
      }
    }

    workflow-template-steps-view {
      display: block;
    }
  `;

    async connectedCallback() {
        super.connectedCallback();

        // Load existing steps if in EDIT or VIEW mode
        // Only load if not already loaded (prevents double-load on page refresh)
        if (this.workflowTemplateId && !this.stepsLoaded) {
            await this.loadStepsData();
        } else if (!this.workflowTemplateId) {
            // ADD mode - new template starts clean (isDirty = false)
            // Dirty state is only set when user actually changes something via markDirty()
            // Steps are optional, so no steps is valid
            this.isDirty = false;
        }

        this.validateAndEmit();
    }

    /**
     * Called after the first render - safe to start tracking dirty state
     * and emit initial validation to parent
     */
    firstUpdated() {
        setTimeout(() => {
            this.isInitializing = false;
            // Re-emit validation now that parent's event listeners are definitely attached
            this.validateAndEmit();
        }, 100);
    }

    /**
     * React to property changes from parent
     */
    updated(changedProperties: Map<string, any>) {
        super.updated(changedProperties);

        if (changedProperties.has("transferType")) {
            // Update formData with new transferType
            this.formData = {
                ...this.formData,
                TransferType: this.transferType,
            };
            this.validateAndEmit();
        }

        // Reload steps when templateData changes (e.g., when parent loads data)
        // Only if we haven't already loaded steps
        if (changedProperties.has("templateData") && this.templateData && this.workflowTemplateId && !this.stepsLoaded) {
            this.loadStepsData();
        }
    }

    /**
     * Load steps data for EDIT/VIEW mode
     * Uses templateData from parent if available (single fetch optimization)
     */
    /**
     * Reverse map Lambda function names to UI function names.
     * The API stores Lambda function names in DynamoDB (e.g., "filepolling"),
     * but the UI dropdown uses user-facing names (e.g., "filedownload").
     * 
     * Mapping from WorkflowStepFunction.cs:
     * - "filemove" → "filecopy" (or "unprocessedfilearchive")
     * - "filepolling" → "filedownload"
     * - "filezip" → "filezip"
     * - "fileencrypt" → "fileencrypt"
     * - "filedelete" → "filedelete"
     * - "filetransferrule" → "rulechecker"
     */
    reverseMapFunctionName(lambdaName: string): string {
        return mapLegacyLambdaFunctionName(lambdaName);
    }

    async loadStepsData() {
        // Prevent double-loading
        if (this.stepsLoaded) {
            return;
        }

        this.apiLoading = true;

        try {
            // Use templateData from parent if available, otherwise fetch (fallback for tests)
            const template = this.templateData || await getRequest(`${Apis.GET_WORKFLOW_TEMPLATE}/${this.workflowTemplateId}`);

            if (template && !template?.details?.length) {
                const steps = template.workflowSteps || [];

                // Map API response to WorkflowStepsView format
                // ⚠️ DynamoDB stores most fields in stepConfiguration object, but wizard expects them flat!
                // ⚠️ DynamoDB stores Lambda function names, but UI uses user-facing names - must reverse map!

                // First, sort steps by stepIndex (handle both camelCase and PascalCase)
                // DynamoDB uses 0-based indexing, UI uses 1-based
                const sortedSteps = [...steps].sort((a: any, b: any) => {
                    const aIndex = a.stepIndex ?? a.StepIndex ?? 0;
                    const bIndex = b.stepIndex ?? b.StepIndex ?? 0;
                    return aIndex - bIndex;
                });

                this.workflowStepsData = sortedSteps.map((step: any, index: number) => {
                    const config = step.stepConfiguration || step.StepConfiguration || {};
                    const srcInfo = step.sourceFileInfo || step.SourceFileInfo || {};
                    const destInfo = step.destinationFileInfo || step.DestinationFileInfo || {};

                    // Get original stepIndex (handle casing), convert from 0-based to 1-based for UI
                    const originalStepIndex = step.stepIndex ?? step.StepIndex ?? index;

                    const uiFunctionName = this.reverseMapFunctionName(
                        step.functionName || step.FunctionName || ""
                    );
                    const flatFileInfo = flattenFileInfoFromStep(
                        { ...step, functionName: uiFunctionName },
                        this.transferType
                    );

                    return {
                        // Use 1-based indexing for UI (DynamoDB uses 0-based)
                        stepIndex: originalStepIndex + 1,
                        stepName: step.stepName || step.StepName || "",
                        // Reverse map Lambda function name to UI function name
                        functionName: uiFunctionName,

                        // Boolean flag (top-level in WorkflowStep) - handle both casings
                        deleteSourceFile: (step.deleteSourceFile ?? step.DeleteSourceFile) ? "true" : "false",

                        // FileMovePattern (top-level in WorkflowStep)
                        // API returns PascalCase from DynamoDB, so check FileMovePattern first
                        fileMovePattern: step.FileMovePattern ?? step.fileMovePattern ?? config.fileMovePattern ?? [],

                        // Page 2 fields from correct source/destination leg
                        locationType: flatFileInfo.locationType,
                        storageType: flatFileInfo.storageType,
                        name: flatFileInfo.name,
                        path: flatFileInfo.path,
                        fileRename: flatFileInfo.fileRename,
                        bucketRename: flatFileInfo.bucketRename,
                        secretName: flatFileInfo.secretName,
                        secretValue: flatFileInfo.secretValue,
                        hostName: flatFileInfo.hostName,
                        port: flatFileInfo.port,
                        userName: flatFileInfo.userName,
                        password: flatFileInfo.password,
                        pgpKey: flatFileInfo.pgpKey,
                        connectionString: flatFileInfo.connectionString,
                        roleARN: flatFileInfo.roleARN,
                        region: flatFileInfo.region,

                        // Preserve both legs for round-trip
                        sourceFileInfo: srcInfo,
                        destinationFileInfo: destInfo,

                        isValidStep: true,
                    };
                });

                // Save original for dirty detection
                this.saveOriginalData();
                this.isDirty = false;
                this.stepsLoaded = true;
            }
        } catch (error) {
            console.error("Failed to load steps:", error);
        } finally {
            this.apiLoading = false;
            this.validateAndEmit();
        }
    }

    /**
     * Save current data as original for dirty detection
     */
    saveOriginalData() {
        this.originalStepsData = JSON.parse(JSON.stringify(this.workflowStepsData));
    }

    /**
     * Mark form as dirty
     */
    markDirty() {
        if (!this.isInitializing) {
            this.isDirty = true;
        }
    }

    /**
     * Handle steps change from WorkflowStepsView
     */
    onStepsChange(e: CustomEvent) {
        const { workflowStepsData, deleteFiles } = e.detail;

        this.workflowStepsData = workflowStepsData || [];
        this.deleteFiles = deleteFiles ?? true;
        this.formData.deleteFiles = this.deleteFiles;

        this.markDirty();
        this.validateAndEmit();
    }

    /**
     * Handle mid-edit state change from WorkflowStepsView
     * Dispatched when step popup opens (midEdit=true) or closes (midEdit=false)
     */
    onStepMidEditChange(e: CustomEvent) {
        this.stepMidEdit = e.detail.midEdit;
        this.validateAndEmit();
    }

    /**
     * Validate and emit validation state
     * Steps are optional (zero steps = valid), but if any step is marked invalid, tab is invalid
     * midEdit is true when step popup (add/edit) is open
     */
    validateAndEmit() {
        // Steps are optional, but if any step is marked invalid, tab is invalid
        const hasInvalidStep = this.workflowStepsData.some(step => step.isValidStep === false);
        const isValid = !hasInvalidStep;
        this.emitValidation(isValid);
    }

    /**
     * Emit validation-change event to parent
     * @param valid - Whether all steps are valid (true if no invalid steps)
     */
    emitValidation(valid: boolean) {
        this.dispatchEvent(
            new CustomEvent("validation-change", {
                detail: {
                    tabName: "steps",
                    valid,
                    dirty: this.isDirty,
                    midEdit: this.stepMidEdit,
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Get steps data for saving
     * Note: DynamoDB uses 0-based stepIndex, so we convert from UI's 1-based to 0-based
     * 
     * IMPORTANT: The API expects a specific structure (see WorkflowStep.cs):
     * - Top level: stepIndex, stepName, functionName, deleteSourceFile, fileMovePattern
     * - destinationFileInfo: FileLocation object with locationType, storageType, path, name, 
     *   fileRename, bucketRename, secretName, secretValue
     * - sourceFileInfo: FileLocation object (for source location, usually empty for templates)
     * 
     * The UI wizard stores fields flat, so we must restructure them here.
     * 
     * CASING: Steps are normalized to camelCase by WorkflowTemplateStepsView.
     * See api-normalizer.ts normalizeStep() for the normalization logic.
     */
    getFormData() {
        // Defensive sort: Ensure steps are ordered by stepIndex before saving.
        // This guards against edge cases where array order might not match stepIndex.
        const sortedSteps = [...this.workflowStepsData].sort((a, b) => {
            const aIndex = a.stepIndex ?? a.StepIndex ?? 0;
            const bIndex = b.stepIndex ?? b.StepIndex ?? 0;
            return aIndex - bIndex;
        });

        const result = {
            steps: sortedSteps.map((step, index) => {
                // Merge flat wizard fields with any existing credential maps so partial edits do not
                // drop other keys (e.g. only Host updated) and flat values always win when non-empty.
                const prevCred: Record<string, any> = {
                    ...(step.secretValue && typeof step.secretValue === "object" ? step.secretValue : {}),
                    ...(step.credentialPlaceholders && typeof step.credentialPlaceholders === "object"
                        ? step.credentialPlaceholders
                        : {}),
                };

                const hostName = step.hostName || step.HostName || "";
                const port = step.port || step.Port || "";
                const userName = step.userName || step.UserName || "";
                const password = step.password || step.Password || "";
                const pgpKey = step.pgpKey || step.PGPKey || "";
                const connectionString = step.connectionString || step.ConnectionString || "";
                const roleARN = step.roleARN || step.RoleARN || "";
                const region = step.region || step.Region || "";

                const merged: Record<string, string> = {};
                const mergeCred = (outKey: string, flatVal: string, prevCamel: string) => {
                    const pascal = prevCamel.charAt(0).toUpperCase() + prevCamel.slice(1);
                    const fromPrev = prevCred[prevCamel] ?? prevCred[pascal];
                    const pv =
                        fromPrev !== undefined && fromPrev !== null && String(fromPrev).trim()
                            ? String(fromPrev)
                            : "";
                    const fv =
                        flatVal !== undefined && flatVal !== null && String(flatVal).trim() ? String(flatVal) : "";
                    const use = fv || pv;
                    if (use) merged[outKey] = use;
                };

                const stepStorageType = step.storageType || step.StorageType || "";
                const usesEndpointCredentials = storageTypeUsesStepCredentials(stepStorageType);

                if (usesEndpointCredentials) {
                    mergeCred("host", hostName, "host");
                    mergeCred("port", port !== undefined && port !== null ? String(port) : "", "port");
                    mergeCred("userName", userName, "userName");
                    mergeCred("password", password, "password");
                    mergeCred("connectionString", connectionString, "connectionString");
                    mergeCred("roleARN", roleARN, "roleARN");
                    mergeCred("region", region, "region");
                }

                if (stringComparison(step.functionName, "fileencrypt")) {
                    mergeCred("pgpKey", pgpKey, "pgpKey");
                }

                delete merged.authType;
                delete merged.AuthType;

                const fileMovePatternValue = step.fileMovePattern || step.FileMovePattern || [];
                const fileLocationAuthType = fileLocationAuthTypeForApiStep({
                    storageType: stepStorageType,
                    AuthType: step.AuthType ?? step.authType,
                });

                const resolvedBucketName = toPersistedTemplatePullSmbBucketName(
                    this.transferType,
                    step.functionName || "",
                    stepStorageType,
                    step.name || ""
                );

                const { sourceFileInfo, destinationFileInfo } = nestFileInfoIntoStep(
                    {
                        locationType: step.locationType || step.LocationType || "",
                        storageType: stepStorageType,
                        path: readStepFilePath(step, this.transferType),
                        name: resolvedBucketName,
                        fileRename: step.fileRename || {},
                        bucketRename: step.bucketRename || "",
                        secretName: step.secretName || "",
                        secretValue: merged,
                        credentialPlaceholders: merged,
                        authType: fileLocationAuthType || undefined,
                    },
                    this.transferType,
                    step.functionName || "",
                    step
                );

                return {
                    // Top-level step fields (match WorkflowStep.cs)
                    // ASP.NET Core deserializes JSON case-insensitively, so camelCase works
                    stepIndex: index, // 0-based for DynamoDB
                    stepName: step.stepName || "",
                    functionName: step.functionName || "",
                    deleteSourceFile: stringComparison(step.deleteSourceFile, "true"),
                    fileMovePattern: fileMovePatternValue,

                    sourceFileInfo,
                    destinationFileInfo,
                };
            }),
            deleteFiles: this.deleteFiles,
        };

        return result;
    }

    render() {
        const isViewMode = this.mode === "VIEW";
        const hasTransferType = !!this.transferType;

        // Build formData for WorkflowStepsView
        const stepsFormData = {
            ...this.formData,
            TransferType: this.transferType?.toLowerCase() || "",
            isEdit: this.mode !== "ADD",
            DeleteFiles: this.deleteFiles,
        };

        return html`
    <div>
        <h2>Workflow Steps</h2>
        <p class="info-text">
            Configure the steps that will be executed when workflows created from this template run. Steps are optional.
        </p>
    
        ${hasTransferType
                ? html`
        <workflow-template-steps-view data-auto-id="stepsView" .steps=${this.workflowStepsData} .isReadOnly=${isViewMode ||
                    this.isReadOnly} .transferType=${this.transferType?.toLowerCase() || ""} .deleteFiles=${this.deleteFiles}
            .formData=${stepsFormData} .variables=${this.variables} .buDisplayName=${this.buDisplayName}
            @onStepsChange=${this.onStepsChange} @variable-added=${this.onVariableAdded}
            @onStepMidEditChange=${this.onStepMidEditChange}>
        </workflow-template-steps-view>
        `
                : html`
        <div class="no-transfer-type">
            <h3>Transfer Type Required</h3>
            <p>Please select a Transfer Type in the Workflow Information tab first.</p>
        </div>
        `}
    </div>
    `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "workflow-template-steps-tab": WorkflowTemplateStepsTab;
    }
}
